import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3, Download, Loader2, FileSpreadsheet, ReceiptText, Check, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { adminApi } from "@/lib/auth";
import { logError } from "@/lib/log";

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const eur = (n) =>
  `${(Number(n) || 0).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")} €`;

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

// Mismo cálculo que el backend: coste horas = horas × €/h, km = km × trayectos.
const recalc = (row) => {
  const kmOneWay = row.km_manual ?? row.km_calc ?? null;
  const kmTotal = Math.round((kmOneWay || 0) * (row.trips || 0) * 100) / 100;
  const kmCost = Math.round(kmTotal * (row.km_rate || 0) * 100) / 100;
  const hourCost = Math.round((row.hours || 0) * (row.hourly_rate || 0) * 100) / 100;
  return {
    ...row,
    km_one_way: kmOneWay,
    km_total: kmTotal,
    km_cost: kmCost,
    hour_cost: hourCost,
    total_cost: Math.round((hourCost + kmCost) * 100) / 100,
  };
};

const downloadBlob = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
};

function NumCell({ value, onSave, testId, step = "0.01", suffix }) {
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => setDraft(value ?? ""), [value]);
  return (
    <div className="flex items-center gap-1">
      <Input
        type="number"
        step={step}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const next = num(draft);
          if (next !== (value ?? null)) onSave(next);
        }}
        className="h-8 w-20 text-right tabular-nums px-1.5"
        data-testid={testId}
      />
      {suffix && <span className="text-[10px] text-zinc-400">{suffix}</span>}
    </div>
  );
}

export default function LiquidacionManager() {
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [receiptFor, setReceiptFor] = useState(null);
  const [receiptNumber, setReceiptNumber] = useState("");
  const [irpf, setIrpf] = useState("15");
  const [issuing, setIssuing] = useState(false);
  const [receiptError, setReceiptError] = useState("");

  const yearOptions = useMemo(() => {
    const y = today.getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, [today]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/admin/liquidacion?year=${year}&month=${month}`);
      setRows((res.data?.rows || []).map(recalc));
    } catch (e) {
      logError("No se pudo cargar la liquidación", e);
      toast.error("No se pudo cargar la liquidación.");
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const saveRow = async (courseId, patch, localPatch) => {
    setRows((prev) =>
      prev.map((r) => (r.course_id === courseId ? recalc({ ...r, ...(localPatch || patch) }) : r)),
    );
    try {
      await adminApi.put(`/admin/liquidacion/${courseId}`, patch);
    } catch (e) {
      logError("No se pudo guardar la línea", e);
      toast.error("No se pudo guardar el cambio.");
      fetchRows();
    }
  };

  const summary = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.instructor_id || r.instructor;
      const a = map.get(key) || {
        instructor_id: r.instructor_id, instructor: r.instructor,
        invoice_type: r.invoice_type, courses: 0, total: 0,
        receipt_total: 0, nomina_total: 0, verified: 0,
      };
      a.courses += 1;
      a.total += r.total_cost || 0;
      if (r.exclude_from_receipt) a.nomina_total += r.total_cost || 0;
      else a.receipt_total += r.total_cost || 0;
      if (r.verified) a.verified += 1;
      map.set(key, a);
    }
    return [...map.values()].sort((a, b) => a.instructor.localeCompare(b.instructor));
  }, [rows]);

  const grandTotal = useMemo(
    () => rows.reduce((acc, r) => acc + (r.total_cost || 0), 0),
    [rows],
  );

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await adminApi.get(
        `/admin/reports/liquidacion?year=${year}&month=${month}`,
        { responseType: "blob" },
      );
      const m = /filename="?([^"]+)"?/.exec(res.headers["content-disposition"] || "");
      downloadBlob(
        res.data,
        (m && m[1]) || `LIQUIDACION_${year}-${String(month).padStart(2, "0")}.xlsx`,
      );
      toast.success("Excel descargado (con fórmulas).");
    } catch (e) {
      logError("Liquidacion download failed", e);
      toast.error("No se pudo descargar el Excel.");
    } finally {
      setDownloading(false);
    }
  };

  const openReceipt = (ins) => {
    setReceiptFor(ins);
    setReceiptNumber(`${year}/01`);
    setIrpf("15");
    setReceiptError("");
  };

  const handleReceipt = async () => {
    if (!receiptFor) return;
    if (!/^\d{4}[/-]\d{1,3}$/.test(receiptNumber.trim())) {
      setReceiptError("El nº de recibo debe ser AAAA/NN (ej. 2026/01).");
      toast.error("El nº de recibo debe ser AAAA/NN (ej. 2026/01).");
      return;
    }
    setReceiptError("");
    setIssuing(true);
    try {
      const res = await adminApi.post(
        "/admin/liquidacion/receipt",
        {
          instructor_id: receiptFor.instructor_id,
          year, month,
          number: receiptNumber.trim(),
          irpf_rate: Number(irpf) || 0,
        },
        { responseType: "blob" },
      );
      const m = /filename="?([^"]+)"?/.exec(res.headers["content-disposition"] || "");
      downloadBlob(res.data, (m && m[1]) || `RECIBO_${receiptNumber.replace("/", "-")}.pdf`);
      toast.success("Recibo generado.");
      setReceiptFor(null);
    } catch (e) {
      logError("No se pudo generar el recibo", e);
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "No se pudo generar el recibo.";
      setReceiptError(msg);
      toast.error(msg);
    } finally {
      setIssuing(false);
    }
  };

  return (
    <section className="space-y-8" data-testid="liquidacion-manager">
      <header className="space-y-2">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500">
          <BarChart3 className="h-3.5 w-3.5" />
          Liquidación
        </div>
        <h2 className="heading-font text-2xl sm:text-3xl font-extrabold text-zinc-950">
          Liquidación mensual de instructores
        </h2>
        <p className="text-sm text-zinc-600 max-w-3xl">
          Corrige horas, tarifas y los kilómetros que te diga el instructor: todo se
          recalcula al momento y se guarda. Las líneas resaltadas son de instructores
          colaboradores con curso bonificado por FUNDAE — esos se pagan por nómina y
          quedan fuera del recibo.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs uppercase tracking-[0.12em] font-semibold text-zinc-600 mb-1">
            Mes
          </label>
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value, 10))}
            className="h-10 border-2 border-zinc-300 focus:border-zinc-900 rounded-md px-3 text-sm font-medium bg-white"
            data-testid="liquidacion-month-select"
          >
            {MONTH_NAMES.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.12em] font-semibold text-zinc-600 mb-1">
            Año
          </label>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="h-10 border-2 border-zinc-300 focus:border-zinc-900 rounded-md px-3 text-sm font-medium bg-white"
            data-testid="liquidacion-year-select"
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <Button
          onClick={handleDownload}
          disabled={downloading}
          variant="outline"
          className="h-10 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white font-semibold"
          data-testid="liquidacion-download-button"
        >
          {downloading
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generando…</>
            : <><FileSpreadsheet className="h-4 w-4 mr-2" />Excel con fórmulas</>}
        </Button>
        <div className="ml-auto text-right">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">Total del mes</div>
          <div className="text-2xl font-extrabold tabular-nums text-zinc-950" data-testid="liquidacion-grand-total">
            {eur(grandTotal)}
          </div>
        </div>
      </div>

      {/* Resumen por instructor + recibo */}
      {!loading && summary.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="liquidacion-summary">
          {summary.map((a) => (
            <div
              key={a.instructor_id || a.instructor}
              className="border-2 border-zinc-200 rounded-md p-4 bg-white"
              data-testid={`liquidacion-summary-${a.instructor_id || "none"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-bold text-zinc-950">{a.instructor}</div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500">
                    {a.invoice_type || "—"} · {a.courses} curso(s) · {a.verified}/{a.courses} comprobados
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-extrabold tabular-nums whitespace-nowrap">
                    {eur(a.total)}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 text-xs">
                <div className="space-y-0.5">
                  <div>Recibo: <b className="tabular-nums">{eur(a.receipt_total)}</b></div>
                  {a.nomina_total > 0 && (
                    <div className="text-amber-700">
                      Nómina: <b className="tabular-nums">{eur(a.nomina_total)}</b>
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  disabled={!a.instructor_id || a.receipt_total <= 0}
                  onClick={() => openReceipt(a)}
                  className="h-8 bg-zinc-950 hover:bg-zinc-800 text-white"
                  data-testid={`liquidacion-receipt-btn-${a.instructor_id || "none"}`}
                >
                  <ReceiptText className="h-3.5 w-3.5 mr-1" />
                  Recibo
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detalle editable */}
      {loading ? (
        <div className="py-12 text-center text-zinc-500">
          <Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin" />
          Calculando kilómetros y tarifas…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center text-zinc-500 py-12 border-2 border-dashed border-zinc-300 rounded-md">
          <p className="font-semibold text-zinc-700">
            No hay cursos en {MONTH_NAMES[month - 1]} de {year}
          </p>
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-md overflow-auto max-h-[70vh]">
          <table className="w-full text-sm min-w-[1180px]">
            <thead className="bg-zinc-100 text-zinc-700 sticky top-0 z-10">
              <tr className="[&>th]:px-2 [&>th]:py-2 [&>th]:font-semibold [&>th]:text-xs [&>th]:uppercase [&>th]:tracking-wider">
                <th className="text-left">Instructor</th>
                <th className="text-left">Curso</th>
                <th className="text-right">Horas</th>
                <th className="text-right">€/h</th>
                <th className="text-right">Coste horas</th>
                <th className="text-right">Km ida</th>
                <th className="text-center">Tray.</th>
                <th className="text-right">Km tot.</th>
                <th className="text-right">€/km</th>
                <th className="text-right">Coste km</th>
                <th className="text-right">TOTAL</th>
                <th className="text-center">Nómina</th>
                <th className="text-center">Comprobado</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.course_id}
                  className={`border-t border-zinc-200 align-middle ${
                    r.is_nomina
                      ? "bg-amber-50 border-l-4 border-l-amber-500"
                      : r.verified ? "bg-emerald-50/40" : "hover:bg-zinc-50"
                  }`}
                  data-testid={`liquidacion-row-${r.course_id}`}
                >
                  <td className="px-2 py-2">
                    <div className="font-semibold text-zinc-900">{r.instructor}</div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                      {r.invoice_type || "—"}
                      {r.fundae && (
                        <span className="px-1 rounded bg-blue-100 text-blue-800 font-bold">FUNDAE</span>
                      )}
                      {r.is_nomina && (
                        <span className="inline-flex items-center gap-0.5 px-1 rounded bg-amber-200 text-amber-900 font-bold">
                          <AlertTriangle className="h-2.5 w-2.5" />NÓMINA
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="text-zinc-900">{r.client || "—"}</div>
                    <div className="text-[11px] text-zinc-500">
                      {r.date_label}{r.days > 1 ? ` · ${r.days} días` : ""}
                      {r.pernocta ? " · pernocta" : ""}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <NumCell
                      value={r.hours}
                      step="0.5"
                      testId={`liquidacion-hours-${r.course_id}`}
                      onSave={(v) => saveRow(r.course_id, { hours: v })}
                    />
                  </td>
                  <td className="px-2 py-2 text-right">
                    <NumCell
                      value={r.hourly_rate}
                      testId={`liquidacion-hourly-${r.course_id}`}
                      onSave={(v) => saveRow(r.course_id, { hourly_rate: v })}
                    />
                  </td>
                  <td
                    className="px-2 py-2 text-right font-semibold tabular-nums"
                    data-testid={`liquidacion-hourcost-${r.course_id}`}
                  >
                    {eur(r.hour_cost)}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <NumCell
                      value={r.km_manual ?? r.km_calc}
                      step="0.1"
                      testId={`liquidacion-km-${r.course_id}`}
                      onSave={(v) => saveRow(r.course_id, { km_one_way: v }, { km_manual: v })}
                    />
                    {r.km_manual !== null && r.km_manual !== undefined && r.km_calc !== null && (
                      <div className="text-[10px] text-zinc-400 mt-0.5">
                        app: {r.km_calc} km
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-center tabular-nums text-zinc-600">{r.trips}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-zinc-700">
                    {r.km_total ? r.km_total.toFixed(1) : "—"}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <NumCell
                      value={r.km_rate}
                      testId={`liquidacion-kmrate-${r.course_id}`}
                      onSave={(v) => saveRow(r.course_id, { km_rate: v })}
                    />
                  </td>
                  <td
                    className="px-2 py-2 text-right font-semibold tabular-nums"
                    data-testid={`liquidacion-kmcost-${r.course_id}`}
                  >
                    {eur(r.km_cost)}
                  </td>
                  <td
                    className="px-2 py-2 text-right font-extrabold tabular-nums text-zinc-950"
                    data-testid={`liquidacion-total-${r.course_id}`}
                  >
                    {eur(r.total_cost)}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!r.exclude_from_receipt}
                      onChange={(e) =>
                        saveRow(r.course_id, { exclude_from_receipt: e.target.checked })
                      }
                      className="h-4 w-4 accent-amber-600 cursor-pointer"
                      title="Se paga por nómina (fuera del recibo)"
                      data-testid={`liquidacion-nomina-${r.course_id}`}
                    />
                  </td>
                  <td className="px-2 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => saveRow(r.course_id, { verified: !r.verified })}
                      className={`h-7 w-7 inline-flex items-center justify-center rounded-md border-2 transition-colors ${
                        r.verified
                          ? "bg-emerald-600 border-emerald-600 text-white"
                          : "border-zinc-300 text-zinc-400 hover:border-emerald-600 hover:text-emerald-600"
                      }`}
                      title={r.verified ? "Comprobado con el instructor" : "Marcar como comprobado"}
                      data-testid={`liquidacion-verified-${r.course_id}`}
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Diálogo del recibo */}
      <Dialog open={!!receiptFor} onOpenChange={(o) => !o && !issuing && setReceiptFor(null)}>
        <DialogContent className="max-w-md" data-testid="liquidacion-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ReceiptText className="h-5 w-5" />
              Recibo de {receiptFor?.instructor}
            </DialogTitle>
            <DialogDescription>
              FORMACIONES mes de {MONTH_NAMES[month - 1].toUpperCase()} de {year}. La fecha
              del recibo es el último día del mes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Nº de recibo *</label>
                <Input
                  value={receiptNumber}
                  onChange={(e) => setReceiptNumber(e.target.value)}
                  placeholder={`${year}/01`}
                  className="h-10"
                  data-testid="liquidacion-receipt-number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">% IRPF</label>
                <Input
                  type="number"
                  step="1"
                  value={irpf}
                  onChange={(e) => setIrpf(e.target.value)}
                  className="h-10"
                  data-testid="liquidacion-receipt-irpf"
                />
              </div>
            </div>
            <div className="rounded-md bg-zinc-50 border border-zinc-200 p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-zinc-600">TOTAL</span>
                <b className="tabular-nums">{eur(receiptFor?.receipt_total)}</b>
              </div>
              <div className="flex justify-between text-zinc-600">
                <span>(-) {Number(irpf) || 0} % IRPF</span>
                <span className="tabular-nums">
                  {eur(((receiptFor?.receipt_total || 0) * (Number(irpf) || 0)) / 100)}
                </span>
              </div>
              <div className="flex justify-between border-t border-zinc-200 pt-1">
                <span className="font-semibold">TOTAL A INGRESAR</span>
                <b className="tabular-nums text-base">
                  {eur((receiptFor?.receipt_total || 0) * (1 - (Number(irpf) || 0) / 100))}
                </b>
              </div>
            </div>
            {receiptError && (
              <p
                className="text-sm font-semibold text-red-600"
                data-testid="liquidacion-receipt-error"
              >
                {receiptError}
              </p>
            )}
            {receiptFor?.nomina_total > 0 && (
              <p className="text-xs text-amber-700">
                No se incluyen {eur(receiptFor.nomina_total)} marcados como pago por nómina.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReceiptFor(null)}
              disabled={issuing}
              data-testid="liquidacion-receipt-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleReceipt}
              disabled={issuing}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="liquidacion-receipt-confirm"
            >
              {issuing
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generando…</>
                : <><Download className="h-4 w-4 mr-2" />Descargar recibo</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
